'use-client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { cn } from '../../utils/cn/cn';

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Root ref={ref} className={className} {...props} />
));

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & { noStyle?: boolean }
>(({ className, noStyle, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      !noStyle &&
        'data-[disabled]:bg-color-separator bg-color-background-base before:transition-background-color before:bg-color-separator relative shadow-xs transition-[margin] before:absolute before:-top-[1px] before:right-0 before:left-0 before:block before:h-[1px] before:transition-opacity first:rounded-t-sm before:first:opacity-0 last:rounded-b-sm data-[state=open]:my-2 data-[state=open]:first:mt-0 data-[state=open]:first:mb-2',
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & { icon?: string }
>(({ className, children, icon = 'icon-emp-arrow-large-down', ...props }, ref) => (
  <AccordionPrimitive.Header className="flex px-2">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'text-size-body1 flex flex-1 items-center justify-between transition-all data-[disabled]:opacity-35 [&[data-state=open]>span]:rotate-180',
        className,
      )}
      {...props}
    >
      <div className="my-1.5">{children}</div>
      <span
        className={`text-color-accordion-toggle-icon ${icon} size-4 shrink-0 transition-transform duration-200`}
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down text-size-body1 overflow-hidden transition-all"
    {...props}
  >
    <div className={cn('px-2 pt-0 pb-4', className)}>{children}</div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export {
  Accordion,
  AccordionItem,
  AccordionTrigger as AccordionSummary,
  AccordionContent as AccordionDetails,
};
